// src/services/ReportService.ts
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  addDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
  orderBy,
} from "firebase/firestore";
import { Report } from "@visitproof/types";
import { db } from "../firebase";
import { clean } from "../utils/utils";

const reportCollectionRef = collection(db, "reports");

export const ReportService = {
  async getReport(reportId: string): Promise<Report | null> {
    const reportDoc = await getDoc(doc(reportCollectionRef, reportId));
    if (reportDoc.exists()) {
      return { id: reportDoc.id, ...reportDoc.data() } as Report;
    }
    return null;
  },

  async getAllReports(accountId: string, status: string): Promise<Report[]> {
    const reportSnapshot = await getDocs(
      query(
        reportCollectionRef,
        where("accountId", "==", accountId),
        where("status", "==", status),
        orderBy("createdAt", "desc"),
      ),
    );
    return reportSnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Report,
    );
  },

  async getReportsByVisitId(visitId: string, accountId: string): Promise<Report[]> {
    const reportSnapshot = await getDocs(
      query(
        reportCollectionRef,
        where("accountId", "==", accountId),
        where("visitId", "==", visitId),
        orderBy("createdAt", "desc"),
      ),
    );
    return reportSnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Report,
    );
  },

  async getReportsBySupervisorId(supervisorId: string, accountId: string): Promise<Report[]> {
    const reportSnapshot = await getDocs(
      query(
        reportCollectionRef,
        where("accountId", "==", accountId),
        where("supervisorId", "==", supervisorId),
        orderBy("createdAt", "desc"),
      ),
    );
    return reportSnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Report,
    );
  },

  async getReportsByCaseId(caseId: string, accountId: string): Promise<Report[]> {
    const reportSnapshot = await getDocs(
      query(
        reportCollectionRef,
        where("accountId", "==", accountId),
        where("caseId", "==", caseId),
        orderBy("createdAt", "desc"),
      ),
    );
    return reportSnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Report,
    );
  },

  async updateReport(
    reportId: string,
    updatedData: Partial<Omit<Report, "id" | "createdAt">>,
  ): Promise<void> {
    const reportDocRef = doc(reportCollectionRef, reportId);
    await updateDoc(reportDocRef, clean({
      ...updatedData,
      updatedAt: Timestamp.now(),
    }));
  },

  async deleteReport(reportId: string): Promise<void> {
    const reportDocRef = doc(reportCollectionRef, reportId);
    await deleteDoc(reportDocRef);
  },

  async createReport(reportData: Omit<Report, "id" | "createdAt" | "updatedAt">): Promise<Report> {
    const newReport = {
      ...reportData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    const docRef = await addDoc(reportCollectionRef, clean(newReport));
    return { id: docRef.id, ...newReport } as Report;
  },
};
